import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RecipeLabelPanel } from "@/components/labels";
import {
  useRecipeShelfLife,
  useRecipeStorageContainers,
  useRecipeTools,
  useUpdateShelfLife,
} from "@/hooks/recipes";
import { getApiErrorMessage } from "@/lib/api/api";
import type { Recipe, RecipeShelfLife, ShelfLifeUnit } from "@/types/recipe";
import {
  storageMethodOptions,
  useStorageMethodNames,
  useStorageMethods,
} from "./storage-methods";
import {
  SHELF_LIFE_UNIT_KEYS,
  shelfLifePayload,
  useShelfLifeUnitNames,
} from "./shelf-life-units";

interface LabelingTabProps {
  recipe: Recipe;
  canManage?: boolean;
}

/**
 * Labeling, on its own.
 *
 * The facts a label states — the use-by rule and how the item is held — are
 * stored on a shelf-life stage, so before T-1002 the only way to change them was
 * to open Shelf Life & Storage and edit that row. They are the same fields
 * either way: saving here writes the same record, and the shelf-life panel shows
 * the new value straight after.
 */
export function LabelingTab({ recipe, canManage = false }: LabelingTabProps) {
  const { t } = useLingui();
  const recipeId = String(recipe.id);
  const { data, isLoading } = useRecipeShelfLife(recipeId);

  // What this recipe is stored in, so the label can carry the kitchen's colour
  // code (T-1005) — a red lid means something, and the sticker should say what.
  const { data: containers } = useRecipeStorageContainers(recipe.id);
  const { data: tools } = useRecipeTools(recipe.id);
  const containerFlags = [
    ...(containers?.data ?? []).map((c) => c.flag),
    ...(tools?.data ?? []).map((tool) => tool.flag),
  ].filter((f): f is string => !!f);

  const rows = data?.data ?? [];
  // The same stage the label has always drawn from: the one marked as needing a
  // label, or the first if none is.
  const labelingRow = rows.find((r) => r.labelingRequired) ?? rows[0];

  return (
    <div className="flex flex-col gap-3 px-6">
      <div className="flex items-center justify-between pl-2">
        <h2 className="text-2xl font-medium text-(--color-text-primary)">{t`Labeling`}</h2>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : labelingRow ? (
        <LabelFactsForm
          key={labelingRow.id}
          recipeId={recipeId}
          row={labelingRow}
          canManage={canManage}
        />
      ) : (
        <p className="rounded-[10px] border border-dashed border-(--color-border) p-6 text-center text-sm text-(--color-text-secondary)">
          {t`A label states a use-by rule and how the item is held, and those belong to a shelf life stage. Add one under Shelf Life & Storage and it will show up here.`}
        </p>
      )}

      <RecipeLabelPanel
        recipe={recipe}
        shelfLife={labelingRow}
        canManage={canManage}
        containerFlags={containerFlags}
      />
    </div>
  );
}

// ── The shared fields ────────────────────────────────────────────────────────

function LabelFactsForm({
  recipeId,
  row,
  canManage,
}: {
  recipeId: string;
  row: RecipeShelfLife;
  canManage: boolean;
}) {
  const { t } = useLingui();
  const storageNames = useStorageMethodNames();
  const storageMethods = useStorageMethods();
  const unitNames = useShelfLifeUnitNames();
  const update = useUpdateShelfLife();

  const [usedByDate, setUsedByDate] = useState(row.usedByDate ?? "");
  const [storageMethod, setStorageMethod] = useState(row.storageMethod ?? "");
  const [labelingRequired, setLabelingRequired] = useState(row.labelingRequired);
  const [lifeValue, setLifeValue] = useState(
    row.shelfLifeValue == null ? "" : String(row.shelfLifeValue),
  );
  const [lifeUnit, setLifeUnit] = useState<ShelfLifeUnit>(row.shelfLifeUnit ?? "DAYS");

  // Take the server's values when a new version of the row arrives — after our
  // own save, or after the shelf-life panel changed the same record — rather
  // than leaving the boxes showing what is no longer stored.
  const serverValue = row.shelfLifeValue == null ? "" : String(row.shelfLifeValue);
  const serverUnit: ShelfLifeUnit = row.shelfLifeUnit ?? "DAYS";
  const serverKey = `${row.usedByDate ?? ""}|${row.storageMethod ?? ""}|${row.labelingRequired}|${serverValue}|${serverUnit}`;
  const [seenKey, setSeenKey] = useState(serverKey);
  if (serverKey !== seenKey) {
    setSeenKey(serverKey);
    setUsedByDate(row.usedByDate ?? "");
    setStorageMethod(row.storageMethod ?? "");
    setLabelingRequired(row.labelingRequired);
    setLifeValue(serverValue);
    setLifeUnit(serverUnit);
  }

  const isDirty =
    usedByDate !== (row.usedByDate ?? "") ||
    storageMethod !== (row.storageMethod ?? "") ||
    labelingRequired !== row.labelingRequired ||
    lifeValue !== serverValue ||
    (lifeValue !== "" && lifeUnit !== serverUnit);

  const save = () =>
    update.mutate(
      {
        recipeId,
        id: String(row.id),
        data: {
          usedByDate: usedByDate || undefined,
          storageMethod: storageMethod || undefined,
          labelingRequired,
          ...shelfLifePayload(lifeValue, lifeUnit),
        },
      },
      {
        onSuccess: () => toast.success(t`Labeling saved`),
        onError: (error) => toast.error(getApiErrorMessage(error, t`Failed to save labeling`)),
      },
    );

  return (
    <div className="flex flex-col gap-4 rounded-[10px] border border-(--color-border)/60 bg-(--color-surface-muted)/40 p-4">
      <div className="flex flex-col gap-1">
        <p className="text-[16px] font-medium">{t`What the label says`}</p>
        <p className="text-sm text-(--color-text-secondary)">
          {t`These are the same fields as the "${row.stage}" shelf life stage — changing them here changes them there.`}
        </p>
      </div>

      <div className="flex flex-col gap-4 md:flex-row">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="labeling-used-by">{t`Used-By Rule`}</Label>
          <Input
            id="labeling-used-by"
            value={usedByDate}
            disabled={!canManage || update.isPending}
            onChange={(e) => setUsedByDate(e.target.value)}
            placeholder={t`e.g. Prep date +3 days, End of service`}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="labeling-storage">{t`Storage method`}</Label>
          <Select
            value={storageMethod}
            disabled={!canManage || update.isPending}
            onValueChange={setStorageMethod}
          >
            <SelectTrigger id="labeling-storage">
              <SelectValue placeholder={t`Select method`} />
            </SelectTrigger>
            <SelectContent>
              {storageMethodOptions(row.storageMethod, storageMethods).map((method) => (
                <SelectItem key={method} value={method}>
                  {storageNames[method] ?? method}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="labeling-shelf-life-value">{t`Shelf life`}</Label>
        <div className="flex items-center gap-2">
          <Input
            id="labeling-shelf-life-value"
            type="number"
            min={1}
            max={999}
            className="w-28"
            value={lifeValue}
            disabled={!canManage || update.isPending}
            onChange={(e) => setLifeValue(e.target.value)}
            placeholder={t`e.g. 2`}
          />
          <Select
            value={lifeUnit}
            disabled={!canManage || update.isPending}
            onValueChange={(v) => setLifeUnit(v as ShelfLifeUnit)}
          >
            <SelectTrigger
              id="labeling-shelf-life-unit"
              aria-label={t`Shelf life unit`}
              className="w-40"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SHELF_LIFE_UNIT_KEYS.map((unit) => (
                <SelectItem key={unit} value={unit}>
                  {unitNames[unit]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-(--color-text-secondary)">
          {t`The label's use-by is counted from the prep time. A hot hold is hours, not days.`}
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Switch
          id="labeling-required-tab"
          checked={labelingRequired}
          disabled={!canManage || update.isPending}
          onCheckedChange={setLabelingRequired}
        />
        <Label htmlFor="labeling-required-tab">{t`Labeling Required`}</Label>
      </div>

      {canManage && (
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={!isDirty || update.isPending} className="gap-1.5">
            {update.isPending && <Loader2 className="size-3.5 animate-spin" />}
            {t`Save labeling`}
          </Button>
          {isDirty && (
            <Button
              variant="ghost"
              disabled={update.isPending}
              onClick={() => {
                setUsedByDate(row.usedByDate ?? "");
                setStorageMethod(row.storageMethod ?? "");
                setLabelingRequired(row.labelingRequired);
                setLifeValue(serverValue);
                setLifeUnit(serverUnit);
              }}
            >
              {t`Discard`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
